import {Injectable, Optional} from '@angular/core';
import {Store} from '@ngrx/store';
import {asyncScheduler, concat, defer, EMPTY, merge, Observable, of, Subject, throwError, timer} from 'rxjs';
import {
  catchError, distinctUntilChanged, filter, groupBy, map, mergeMap, scan, startWith, switchMap, take, throttleTime, timeout
} from 'rxjs/operators';
import {CommandService} from '@core/services/command.service';
import {GraphQLService} from '@core/services/graphql.service';
import {SocketClusterService} from '@core/services/socket-cluster.service';
import {PlatformCommand} from '@shared/commands/command-base';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getUserInfo} from '@shared/store/selectors/security.selector';
import type {MessengerSignalsPort} from './signals.port';
import {TypingMember} from './messenger.models';
import {CollaborationConfig} from '../store/collaboration.config';
import {CollaborationQuery} from '../store/collaboration.query';
import {CollaborationState} from '../store/collaboration.reducer';
import {getCurrentChatConversation} from '../store/collaboration.selector';

/**
 * The signals port on the server: B-09, where unread starts (ITP-E-206-01), B-17, the delivered tick (ITP-E-207-01), and
 * B-18, who is typing (ITP-E-207-02).
 */
@Injectable({providedIn: 'root'})
export class MessengerSignalsReal implements MessengerSignalsPort {
  /**
   * B-17: how far apart one conversation's delivered reports go out. The server takes one a second per member and
   * conversation, timed as each arrives, and drops the rest; the quarter second on top keeps network jitter from landing a
   * trailing report inside that second, where it would be dropped and the last sequence lost.
   */
  static readonly deliveredReportSpacingMs = 1250;

  /** A platform command does not fail on a lost connection, so a bounded wait stands in for one (as the organisation's do). */
  static readonly commandTimeoutMs = 30000;

  /** B-18: how long a member shows as typing after their last `ConversationTyping`. */
  static readonly typingShownMs = 5000;

  /** B-18: one `SetTypingCommand` per conversation in this long, the server's window per member and conversation. */
  static readonly typingReportSpacingMs = 3000;

  /** B-17: each conversation's delivered reports as they are asked for, before they are spaced out. */
  private readonly deliveredReports = new Map<string, Subject<number>>();

  /** B-18: when each conversation's typing was last reported. */
  private readonly typingReportedAt = new Map<string, number>();

  /**
   * `commandService` sends the delivered reports (B-17) and typing (B-18), `graphQLService` reads `deliveredUpTo`,
   * `socketClusterService` hands over the `ConversationDelivered` and `ConversationTyping` events, and `securityStore` names
   * the reader, whose own typing is never shown. They are optional because the port builds this adapter for every method,
   * so a component that uses none of them can be built without them; the app always has them (CoreModule).
   */
  constructor(private collaborationStore: Store<CollaborationState>,
              @Optional() private commandService?: CommandService,
              @Optional() private graphQLService?: GraphQLService,
              @Optional() private socketClusterService?: SocketClusterService,
              @Optional() private securityStore?: Store<SecurityState>) {
  }

  /**
   * B-09 (ITP-E-206-01): `firstUnreadSequence` as the server answered it on the conversation by id
   * (`getChatConversationById`), read from the open conversation in the store. That load is the one taken before the
   * conversation is marked read, so it still says where unread starts. Null when the server answers none (all read), or
   * when the open conversation is another one.
   */
  firstUnreadSequence(conversationId: string): Observable<number | null> {
    return this.collaborationStore.select(getCurrentChatConversation).pipe(take(1),
      map(conversation => conversation?.id === conversationId && typeof conversation.firstUnreadSequence === 'number'
        ? conversation.firstUnreadSequence : null));
  }

  /**
   * B-17 (ITP-E-207-01): `deliveredUpTo` on the conversation by id, the sequence every other current member has received,
   * read or not; then read again each time a `ConversationDelivered` of the conversation could raise it (another member's
   * report past it: one at or below it was not the lowest, and the reader's own never counts). Never lower than an answer
   * already given. The events are a stream held here, never written to the store. Null while the server answers none: nobody
   * else is in the conversation. ITP-E-240-01: with ChatPrivateDelivery off the events come on the company channel, the
   * reader's own reports and every other chat's too, which this keeps out.
   */
  deliveredUpTo(conversationId: string): Observable<number | null> {
    if (!conversationId) {
      return of(null);
    }
    if (!this.graphQLService) {
      return throwError(() => new Error('Messenger B-17: there is no GraphQL service to read delivery with.'));
    }
    return defer(() => {
      let known: number | null = null;
      const raised = this.readerEmail().pipe(
        switchMap(reader => (this.socketClusterService?.onConversationDelivered() ?? EMPTY).pipe(
          filter(delivered => delivered?.conversationId === conversationId && delivered.email?.toLowerCase() !== reader
            && (known === null || delivered.sequence > known)))),
        switchMap(() => this.readDeliveredUpTo(conversationId).pipe(catchError(() => EMPTY))));
      return merge(this.readDeliveredUpTo(conversationId), raised).pipe(
        map(sequence => {
          // A member who joins has received nothing yet, but what reached everyone before them still did.
          known = sequence === null ? known : Math.max(known ?? sequence, sequence);
          return known;
        }),
        distinctUntilChanged());
    });
  }

  /**
   * B-17 (ITP-E-207-01): `DeliveredUpToCommand(conversationId, sequence)`, the messages up to `sequence` reached this device.
   * Spaced out per conversation, leading and trailing: the first report goes out at once, the ones asked for while it is
   * spaced collapse into their highest, which goes out when the spacing ends, so the last sequence always goes out. Answers
   * as soon as the report is queued; a report the server does not take is dropped, as the next one carries a higher sequence.
   */
  markDelivered(conversationId: string, sequence: number): Observable<void> {
    return defer(() => {
      if (conversationId && Number.isFinite(sequence)) {
        this.reportsOf(conversationId).next(sequence);
      }
      return of(undefined);
    });
  }

  /**
   * B-18 (ITP-E-207-02): who is typing in the conversation now, from the `ConversationTyping` events (on the company channel
   * with ChatPrivateDelivery off, on the personal channel with it on: ITP-E-240-01). An event lists its member for
   * `typingShownMs` and a repeat starts that again; the reader is never listed, nor anyone typing in another conversation. The
   * list starts empty and is held here, never written to the store.
   */
  typing(conversationId: string): Observable<TypingMember[]> {
    if (!conversationId) {
      return of([]);
    }
    const events = this.socketClusterService?.onConversationTyping() ?? EMPTY;
    return this.readerEmail().pipe(
      switchMap(reader => events.pipe(
        filter(event => event?.conversationId === conversationId && !!event.email && event.email.toLowerCase() !== reader),
        groupBy(event => event.email.toLowerCase()),
        mergeMap(member => member.pipe(switchMap(event => concat(
          of({email: member.key, typing: {email: event.email, name: event.name} as TypingMember}),
          timer(MessengerSignalsReal.typingShownMs).pipe(map(() => ({email: member.key, typing: null as TypingMember}))))))),
        scan((members: TypingMember[], change) => {
          const at = members.findIndex(typing => typing.email.toLowerCase() === change.email);
          if (!change.typing) {
            return members.filter((_, index) => index !== at);
          }
          return at < 0 ? [...members, change.typing] : members.map((typing, index) => index === at ? change.typing : typing);
        }, [] as TypingMember[]),
        startWith([] as TypingMember[]),
        distinctUntilChanged((a, b) => a.length === b.length
          && a.every((typing, index) => typing.email === b[index].email && typing.name === b[index].name)))));
  }

  /**
   * B-18 (ITP-E-207-02): `SetTypingCommand(conversationId)`, this user is typing. Sent at once, then not again for that
   * conversation until `typingReportSpacingMs` has passed; a call inside it answers at once and sends nothing.
   */
  setTyping(conversationId: string): Observable<void> {
    return defer(() => {
      const now = Date.now();
      const reportedAt = this.typingReportedAt.get(conversationId);
      if (!conversationId || (reportedAt !== undefined && now - reportedAt < MessengerSignalsReal.typingReportSpacingMs)) {
        return of(undefined);
      }
      if (!this.commandService) {
        return throwError(() => new Error('Messenger B-18: there is no command service to report typing with.'));
      }
      this.typingReportedAt.set(conversationId, now);
      const command = new PlatformCommand('SetTypingCommand', 'CollaborationApi');
      command.fieldValues = {ConversationId: conversationId};
      command.waitForResponse = true;
      // A signal the composer sends while the person types, not an action of theirs (MISN-E-71-06).
      command.isUserAction = false;
      return this.commandService.executePlatformCommand(command).pipe(
        take(1),
        timeout(MessengerSignalsReal.commandTimeoutMs),
        map(() => undefined));
    });
  }

  /** The reader's email, lower-cased; null when it is not known. */
  private readerEmail(): Observable<string | null> {
    return (this.securityStore?.select(getUserInfo) ?? of(null)).pipe(take(1), map(user => user?.email?.toLowerCase() || null));
  }

  private readDeliveredUpTo(conversationId: string): Observable<number | null> {
    return this.graphQLService.executePostRequest(CollaborationConfig.CollaborationApiKey,
      CollaborationQuery.deliveredUpTo(conversationId)).pipe(
      map(response => {
        const conversation = response?.data?.chatById;
        if (!conversation) {
          throw new Error(`Messenger B-17: conversation ${conversationId} answered no delivery.`);
        }
        return typeof conversation.deliveredUpTo === 'number' ? conversation.deliveredUpTo : null;
      }));
  }

  /** The conversation's reports, spaced out and sent: made on its first report and kept for the page's life. */
  private reportsOf(conversationId: string): Subject<number> {
    let reports = this.deliveredReports.get(conversationId);
    if (!reports) {
      reports = new Subject<number>();
      reports.pipe(
        scan((highest, sequence) => Math.max(highest, sequence), Number.NEGATIVE_INFINITY),
        distinctUntilChanged(),
        throttleTime(MessengerSignalsReal.deliveredReportSpacingMs, asyncScheduler, {leading: true, trailing: true}),
        mergeMap(sequence => this.reportDelivered(conversationId, sequence).pipe(catchError(() => EMPTY)))
      ).subscribe();
      this.deliveredReports.set(conversationId, reports);
    }
    return reports;
  }

  private reportDelivered(conversationId: string, sequence: number): Observable<void> {
    if (!this.commandService) {
      return throwError(() => new Error('Messenger B-17: there is no command service to report delivery with.'));
    }
    return defer(() => {
      const command = new PlatformCommand('DeliveredUpToCommand', 'CollaborationApi');
      command.fieldValues = {ConversationId: conversationId, Sequence: sequence};
      command.waitForResponse = true;
      // The browser's own bookkeeping, not something the person did (MISN-E-71-06).
      command.isUserAction = false;
      return this.commandService.executePlatformCommand(command);
    }).pipe(
      take(1),
      timeout(MessengerSignalsReal.commandTimeoutMs),
      map(() => undefined));
  }
}
